"use client";

import { LuRefreshCw } from "react-icons/lu";
import type { Reading } from "./types";

/** Colour bands are the ones the home page's latency meter already uses. */
function band(ms: number): { color: string; bars: number } {
    if (ms < 80) return { color: "#4ade80", bars: 4 };
    if (ms < 150) return { color: "#facc15", bars: 3 };
    if (ms < 250) return { color: "#fb923c", bars: 2 };
    return { color: "#f87171", bars: 1 };
}

export function latencyText(r: Reading, soon = false): string {
    if (soon) return "Soon";
    if (r === "fail") return "n/a";
    if (typeof r === "number") return `${r} ms`;
    return "…";
}

export function latencyColor(r: Reading): string {
    return typeof r === "number" ? band(r).color : "#6b7280";
}

/** Four rising bars, filled to match the reading's latency band. */
export function SignalBars({ reading }: { reading: Reading }) {
    const filled = typeof reading === "number" ? band(reading).bars : 0;
    const color = latencyColor(reading);
    return (
        <svg width="14" height="11" viewBox="0 0 16 12" fill="none" aria-hidden="true" className="shrink-0">
            {[0, 1, 2, 3].map((i) => {
                const h = (i + 1) * 3;
                return (
                    <rect
                        key={i}
                        x={i * 4}
                        y={12 - h}
                        width="3"
                        height={h}
                        rx="0.75"
                        fill={i < filled ? color : "rgba(255,255,255,0.12)"}
                    />
                );
            })}
        </svg>
    );
}

/**
 * Reading + bars, with a refresh control that re-probes this location. Rendered
 * as a button so the number is never stale without a way to fix it.
 */
export function PingChip({
    reading,
    soon = false,
    onRetest,
}: {
    reading: Reading;
    soon?: boolean;
    onRetest?: () => void;
}) {
    const text = latencyText(reading, soon);
    const busy = reading === null && !soon;

    if (soon || !onRetest) {
        return (
            <span className="inline-flex items-center gap-1.5 font-poppins text-xs tabular-nums" style={{ color: latencyColor(reading) }}>
                {!soon && <SignalBars reading={reading} />}
                {text}
            </span>
        );
    }

    return (
        <button
            onClick={onRetest}
            disabled={busy}
            title="Re-test latency from your connection"
            aria-label="Re-test latency from your connection"
            className="inline-flex items-center gap-1.5 font-poppins text-xs tabular-nums rounded-full px-2 py-1 transition-colors hover:bg-white/10 disabled:opacity-70"
            style={{ color: latencyColor(reading) }}
        >
            <SignalBars reading={reading} />
            {text}
            <LuRefreshCw size={11} className={`text-gray-500 ${busy ? "animate-spin" : ""}`} />
        </button>
    );
}
